import { priceGroupWriteSchema } from './price-group.js';
import { z } from 'zod';
import { BUSINESS_TERM_SCOPES, DOCUMENT_TEXT_PLACEMENTS, NUMBER_RANGE_RESETS, SETUP_DATA_ENTITY_TYPES, SETUP_DATA_TRANSFER_DECISIONS, SETUP_PURCHASE_NUMBER_RANGE_KEYS, SETUP_SALES_NUMBER_RANGE_KEYS, SETUP_VERIFICATION_KINDS, SETUP_VERIFICATION_STATUSES, TENANT_SETUP_STATUSES, TENANT_SETUP_STEPS, } from '../constants/index.js';
import { companyProfileSchema, companyProfileUpdateSchema } from './company.js';
import { countryCodeSchema, currencyCodeSchema, idSchema, isoDateTimeSchema } from './common.js';
import { documentTypeSchema, incotermSchema, roleKeySchema, taxRateTypeSchema } from './enums.js';
import { supportedLocaleSchema } from './plm-material.js';
import { twoFactorMethodSchema, userCreateSchema, userCreateResponseSchema } from './security.js';
export const tenantSetupStepSchema = z.enum(TENANT_SETUP_STEPS);
export const tenantSetupStatusSchema = z.enum(TENANT_SETUP_STATUSES);
export const setupVerificationKindSchema = z.enum(SETUP_VERIFICATION_KINDS);
export const setupVerificationStatusSchema = z.enum(SETUP_VERIFICATION_STATUSES);
export const setupDataTransferDecisionSchema = z.enum(SETUP_DATA_TRANSFER_DECISIONS);
export const setupDataEntityTypeSchema = z.enum(SETUP_DATA_ENTITY_TYPES);
export const businessTermScopeSchema = z.enum(BUSINESS_TERM_SCOPES);
export const documentTextPlacementSchema = z.enum(DOCUMENT_TEXT_PLACEMENTS);
export const numberRangeResetSchema = z.enum(NUMBER_RANGE_RESETS);
const localizedNameSchema = z.object({
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1000).nullable().default(null),
});
/** Every setup-created catalogue entry is complete in every active ERP language. */
export const setupTranslationsSchema = z
    .object({
    de: localizedNameSchema,
    en: localizedNameSchema,
    tr: localizedNameSchema,
    fr: localizedNameSchema,
    es: localizedNameSchema,
})
    .strict();
export const setupTextTranslationsSchema = z
    .object({
    de: z.string().trim().min(1).max(20_000),
    en: z.string().trim().min(1).max(20_000),
    tr: z.string().trim().min(1).max(20_000),
    fr: z.string().trim().min(1).max(20_000),
    es: z.string().trim().min(1).max(20_000),
})
    .strict();
const technicalCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_-]{0,39}$/u);
export const setupBankAccountSchema = z.object({
    id: idSchema.optional(),
    key: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z][a-z0-9-]{0,39}$/u),
    accountHolder: z.string().trim().min(1).max(160),
    bankName: z.string().trim().min(1).max(160),
    iban: z
        .string()
        .trim()
        .toUpperCase()
        .transform((value) => value.replace(/\s+/gu, ''))
        .pipe(z.string().regex(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/u)),
    bic: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/u)
        .nullable(),
    currency: currencyCodeSchema,
    isDefault: z.boolean(),
});
function exactlyOneDefault(rows) {
    return rows.filter((row) => row.isDefault).length === 1;
}
export const setupCompanyStepSchema = z.object({
    company: companyProfileUpdateSchema,
    locale: supportedLocaleSchema,
    timezone: z.string().trim().min(1).max(100),
    currency: currencyCodeSchema,
    bankAccounts: z
        .array(setupBankAccountSchema)
        .min(1)
        .max(20)
        .refine(exactlyOneDefault, 'Genau ein Bankkonto muss als Standard markiert sein.'),
});
export const setupNumberRangeSchema = z.object({
    key: z.string().trim().min(1).max(50),
    prefix: z.string().trim().max(30),
    suffix: z.string().trim().max(30),
    padding: z.number().int().min(1).max(12),
    startingNumber: z.number().int().min(1).max(2_000_000_000),
    resetInterval: numberRangeResetSchema,
    yearFormat: z.enum(['YY', 'YYYY']),
});
function exactRangeKeys(expected) {
    return (ranges) => ranges.length === expected.length &&
        new Set(ranges.map((range) => range.key)).size === expected.length &&
        expected.every((key) => ranges.some((range) => range.key === key));
}
export const setupPaymentTermSchema = z.object({
    id: idSchema.optional(),
    code: technicalCodeSchema,
    scope: businessTermScopeSchema,
    translations: setupTranslationsSchema,
    dueDays: z.number().int().min(0).max(3650),
    cashDiscountPct: z.number().min(0).max(100),
    cashDiscountDays: z.number().int().min(0).max(3650),
    isDefault: z.boolean(),
});
export const setupDeliveryTermSchema = z.object({
    id: idSchema.optional(),
    code: technicalCodeSchema,
    scope: businessTermScopeSchema,
    translations: setupTranslationsSchema,
    incoterm: incotermSchema.nullable(),
    isDefault: z.boolean(),
});
export const setupTaxRateSchema = z.object({
    id: idSchema.optional(),
    code: technicalCodeSchema,
    translations: setupTranslationsSchema,
    taxRateType: taxRateTypeSchema,
    ratePct: z.number().min(0).max(100),
    countryCode: countryCodeSchema.nullable(),
    isDefault: z.boolean(),
});
export const setupStandardTextSchema = z.object({
    id: idSchema.optional(),
    key: technicalCodeSchema,
    documentType: documentTypeSchema,
    placement: documentTextPlacementSchema,
    translations: setupTextTranslationsSchema,
    isDefault: z.boolean(),
});
export const setupSalesStepSchema = z
    .object({
    numberRanges: z
        .array(setupNumberRangeSchema)
        .refine(exactRangeKeys(SETUP_SALES_NUMBER_RANGE_KEYS), 'Alle Verkaufsnummernkreise fehlen.'),
    paymentTerms: z
        .array(setupPaymentTermSchema)
        .min(1)
        .max(50)
        .refine(exactlyOneDefault, 'Genau eine Zahlungsbedingung muss Standard sein.'),
    deliveryTerms: z
        .array(setupDeliveryTermSchema)
        .min(1)
        .max(50)
        .refine(exactlyOneDefault, 'Genau eine Lieferbedingung muss Standard sein.'),
    taxRates: z
        .array(setupTaxRateSchema)
        .min(1)
        .max(50)
        .refine(exactlyOneDefault, 'Genau ein Steuersatz muss Standard sein.'),
    standardTexts: z.array(setupStandardTextSchema).min(1).max(100),
    documentTemplateIds: z.array(idSchema).max(50),
    useSystemDocumentTemplate: z.boolean(),
})
    .refine((value) => value.useSystemDocumentTemplate || value.documentTemplateIds.length > 0, 'Eine Belegvorlage oder die Systemvorlage muss bewusst gewählt werden.');
export const setupPurchasingStepSchema = z.object({
    numberRanges: z
        .array(setupNumberRangeSchema)
        .refine(exactRangeKeys(SETUP_PURCHASE_NUMBER_RANGE_KEYS), 'Alle Einkaufsnummernkreise fehlen.'),
    defaultPaymentTermDays: z.number().int().min(0).max(3650),
    defaultIncoterm: incotermSchema.nullable(),
    creditorAccountRangeStart: z.number().int().min(10_000).max(999_999_999),
    supplierPaymentTermCodes: z.array(technicalCodeSchema).max(50),
    requirePurchaseOrderForGoodsReceipt: z.boolean(),
    defaultGoodsReceiptWarehouseCode: technicalCodeSchema,
});
export const setupUnitSchema = z.object({
    id: idSchema.optional(),
    code: technicalCodeSchema,
    translations: setupTranslationsSchema,
    decimalPlaces: z.number().int().min(0).max(6),
    isDefault: z.boolean(),
});
export const setupWarehouseSchema = z.object({
    id: idSchema.optional(),
    code: technicalCodeSchema,
    translations: setupTranslationsSchema,
    street: z.string().trim().max(160).nullable(),
    postalCode: z.string().trim().max(20).nullable(),
    city: z.string().trim().max(120).nullable(),
    countryCode: countryCodeSchema,
    storageLocations: z.array(technicalCodeSchema).max(500),
    isDefault: z.boolean(),
});
export const setupPriceListSchema = z.object({
    id: idSchema.optional(),
    code: priceGroupWriteSchema.shape.code,
    translations: setupTranslationsSchema,
    currency: currencyCodeSchema.nullable(),
    isDefault: z.boolean(),
});
export const setupInventoryStepSchema = z
    .object({
    units: z
        .array(setupUnitSchema)
        .min(1)
        .max(100)
        .refine(exactlyOneDefault, 'Genau eine Einheit muss Standard sein.'),
    warehouses: z
        .array(setupWarehouseSchema)
        .min(1)
        .max(100)
        .refine(exactlyOneDefault, 'Genau ein Lager muss Standard sein.'),
    existingCategoryIds: z.array(idSchema).max(500),
    newCategoryNames: z.array(z.string().trim().min(1).max(120)).max(100),
    priceLists: z
        .array(setupPriceListSchema)
        .min(1)
        .max(100)
        .refine(exactlyOneDefault, 'Genau eine Preisliste muss Standard sein.'),
    inventoryTrackingEnabled: z.boolean(),
    defaultBatchManaged: z.boolean(),
    defaultShelfLifeManaged: z.boolean(),
    defaultShelfLifeDays: z.number().int().min(1).max(36_500).nullable(),
})
    .superRefine((value, context) => {
    if (value.existingCategoryIds.length === 0 && value.newCategoryNames.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['newCategoryNames'],
            message: 'Mindestens eine Artikelkategorie ist erforderlich.',
        });
    }
    if (value.defaultShelfLifeManaged && value.defaultShelfLifeDays === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['defaultShelfLifeDays'],
            message: 'Bei MHD-Führung ist eine Standardhaltbarkeit erforderlich.',
        });
    }
});
export const setupUsersStepSchema = z.object({
    allowedMethods: z.array(twoFactorMethodSchema).min(1).max(2),
    isTwoFactorRequired: z.boolean(),
    requiredRoles: z.array(roleKeySchema),
    privilegedRolesRequireTotp: z.boolean(),
    emailTwoFactorEnabled: z.boolean(),
    emailOtpValidityMinutes: z.number().int().min(5).max(15),
    emailOtpResendCooldownSeconds: z.number().int().min(30).max(300),
    confirmed: z.literal(true),
});
export const setupInvitationSchema = userCreateSchema;
export const setupInvitationResponseSchema = userCreateResponseSchema;
export const setupCommunicationStepSchema = z.object({
    emailEnabled: z.boolean(),
    defaultEmailSender: z.string().trim().toLowerCase().email().max(200).nullable(),
    telephonyEnabled: z.boolean(),
    newsletterEnabled: z.boolean(),
    confirmed: z.literal(true),
});
export const setupDataTransferStepSchema = z
    .object({
    decision: setupDataTransferDecisionSchema,
    entityTypes: z.array(setupDataEntityTypeSchema).max(SETUP_DATA_ENTITY_TYPES.length),
    notes: z.string().trim().max(5000).nullable(),
})
    .refine((value) => value.decision === 'NO_DATA' || value.entityTypes.length > 0, 'Für eine Datenübernahme muss mindestens ein Datenbereich gewählt werden.');
export const setupStepSaveSchema = z.discriminatedUnion('step', [
    z.object({ step: z.literal('COMPANY'), data: setupCompanyStepSchema }),
    z.object({ step: z.literal('SALES'), data: setupSalesStepSchema }),
    z.object({ step: z.literal('PURCHASING'), data: setupPurchasingStepSchema }),
    z.object({ step: z.literal('INVENTORY'), data: setupInventoryStepSchema }),
    z.object({ step: z.literal('USERS'), data: setupUsersStepSchema }),
    z.object({ step: z.literal('COMMUNICATION'), data: setupCommunicationStepSchema }),
    z.object({ step: z.literal('DATA_TRANSFER'), data: setupDataTransferStepSchema }),
]);
export const setupNavigationSchema = z.object({ step: tenantSetupStepSchema });
export const setupEmailVerificationRequestSchema = z.object({
    recipient: z.string().trim().toLowerCase().email().max(200),
});
export const setupReleaseSchema = z.object({
    confirmed: z.literal(true),
    optimisticVersion: z.number().int().positive(),
});
export const setupIssueSchema = z.object({
    code: z.string(),
    severity: z.enum(['ERROR', 'WARNING']),
    step: tenantSetupStepSchema,
    field: z.string().nullable(),
    message: z.string(),
    link: z.string().nullable(),
});
export const setupStepSummarySchema = z.object({
    step: tenantSetupStepSchema,
    completed: z.boolean(),
    issueCount: z.number().int().min(0),
    warningCount: z.number().int().min(0),
    link: z.string(),
});
export const setupVerificationSchema = z.object({
    id: idSchema,
    kind: setupVerificationKindSchema,
    status: setupVerificationStatusSchema,
    attachmentId: idSchema.nullable(),
    recipient: z.string().nullable(),
    messageId: z.string().nullable(),
    errorMessage: z.string().nullable(),
    configurationVersion: z.number().int().positive(),
    details: z.record(z.unknown()),
    performedById: idSchema,
    performedAt: isoDateTimeSchema,
});
export const setupRoleOptionSchema = z.object({
    id: idSchema,
    key: roleKeySchema,
    name: z.string(),
});
/**
 * Leseform des Assistenten. Anders als die Save-Schemas bildet sie bewusst auch
 * eine noch unvollstaendige Ersteinrichtung ab, ohne erfundene Pflichtwerte an
 * den Client zu liefern.
 */
export const setupCompanyDraftSchema = z.object({
    company: companyProfileSchema.omit({ id: true, updatedAt: true }),
    locale: supportedLocaleSchema,
    timezone: z.string(),
    currency: currencyCodeSchema,
    bankAccounts: z.array(setupBankAccountSchema),
});
export const setupSalesDraftSchema = z.object({
    numberRanges: z.array(setupNumberRangeSchema),
    paymentTerms: z.array(setupPaymentTermSchema),
    deliveryTerms: z.array(setupDeliveryTermSchema),
    taxRates: z.array(setupTaxRateSchema),
    standardTexts: z.array(setupStandardTextSchema),
    documentTemplateIds: z.array(idSchema),
    useSystemDocumentTemplate: z.boolean(),
});
export const setupPurchasingDraftSchema = z.object({
    numberRanges: z.array(setupNumberRangeSchema),
    defaultPaymentTermDays: z.number().int().min(0).max(3650),
    defaultIncoterm: incotermSchema.nullable(),
    creditorAccountRangeStart: z.number().int().min(10_000).max(999_999_999),
    supplierPaymentTermCodes: z.array(technicalCodeSchema),
    requirePurchaseOrderForGoodsReceipt: z.boolean(),
    defaultGoodsReceiptWarehouseCode: z.string(),
});
export const setupInventoryDraftSchema = z.object({
    units: z.array(setupUnitSchema),
    warehouses: z.array(setupWarehouseSchema),
    existingCategoryIds: z.array(idSchema),
    newCategoryNames: z.array(z.string()),
    priceLists: z.array(setupPriceListSchema),
    inventoryTrackingEnabled: z.boolean(),
    defaultBatchManaged: z.boolean(),
    defaultShelfLifeManaged: z.boolean(),
    defaultShelfLifeDays: z.number().int().nullable(),
});
export const setupUsersDraftSchema = setupUsersStepSchema.extend({ confirmed: z.boolean() });
export const setupCommunicationDraftSchema = setupCommunicationStepSchema.extend({
    confirmed: z.boolean(),
});
export const setupDashboardSchema = z.object({
    id: idSchema,
    status: tenantSetupStatusSchema,
    currentStep: tenantSetupStepSchema,
    completedSteps: z.array(tenantSetupStepSchema),
    progressPercent: z.number().int().min(0).max(100),
    canRelease: z.boolean(),
    releasedAt: isoDateTimeSchema.nullable(),
    releasedById: idSchema.nullable(),
    optimisticVersion: z.number().int().positive(),
    configurationVersion: z.number().int().positive(),
    steps: z.array(setupStepSummarySchema).length(TENANT_SETUP_STEPS.length),
    issues: z.array(setupIssueSchema),
    configuration: z.object({
        company: setupCompanyDraftSchema,
        sales: setupSalesDraftSchema,
        purchasing: setupPurchasingDraftSchema,
        inventory: setupInventoryDraftSchema,
        users: setupUsersDraftSchema,
        communication: setupCommunicationDraftSchema,
        dataTransfer: setupDataTransferStepSchema.nullable(),
    }),
    context: z.object({
        activeUserCount: z.number().int().min(0),
        roles: z.array(setupRoleOptionSchema),
        telephonyConfiguredUserCount: z.number().int().min(0),
        newsletterConfigured: z.boolean(),
        newsletterVerifiedDomainCount: z.number().int().min(0),
        completedImportCount: z.number().int().min(0),
        availableDocumentTemplateIds: z.array(idSchema),
        availableDocumentTemplates: z.array(z.object({
            id: idSchema,
            name: z.string(),
            documentType: z.string(),
            isDefault: z.boolean(),
        })),
        availableCategories: z.array(z.object({ id: idSchema, name: z.string() })),
    }),
    verifications: z.array(setupVerificationSchema),
});
//# sourceMappingURL=setup.js.map